import { GAME, TEX } from '../config/constants';
import { ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { t } from '../services/textos';

/** Bordas esquerda e direita do que se desenha: a área de 1280 mais a margem de tela larga (celular). */
export const BORDA_ESQ = -GAME.MARGEM;
export const BORDA_DIR = GAME.WIDTH + GAME.MARGEM;

/**
 * Imagem de fundo do tamanho do jogo (1280×800) com uma cópia espelhada de cada lado: em tela
 * mais larga que 16:10 o fundo continua além da área central, sem emenda. Em 16:10 as cópias
 * ficam fora do canvas.
 */
export function desenharImagemDeFundo (scene, textura) {
    const x = [GAME.CENTER_X - GAME.WIDTH, GAME.CENTER_X + GAME.WIDTH];
    const lados = x.map((posicao) => scene.add.image(posicao, GAME.CENTER_Y, textura).setFlipX(true));
    const centro = scene.add.image(GAME.CENTER_X, GAME.CENTER_Y, textura);
    return [...lados, centro];
}

/** Fundo grafite + logo FIAT Arcade, comuns às telas de entrada. */
export function desenharFundo (scene, { logoY = 133.5 } = {}) {
    if (scene.textures.exists(TEX.BG_GRAFITE)) {
        desenharImagemDeFundo(scene, TEX.BG_GRAFITE);
    } else {
        scene.cameras.main.setBackgroundColor(ARCADE_HEX.GRAFITE);
    }
    if (scene.textures.exists(TEX.LOGO_FIAT_ARCADE)) {
        scene.add.image(639.5, logoY, TEX.LOGO_FIAT_ARCADE);
    }
}

/** Logo "Novo Fiat Argo X" (branca) centrada em `x`, `largura` px de largura (proporção 1920×284 do arquivo). */
export function desenharLogoArgoX (scene, y, largura = 420, x = 639.5) {
    if (!scene.textures.exists(TEX.LOGO_ARGO_X)) return null;
    return scene.add.image(x, y, TEX.LOGO_ARGO_X).setDisplaySize(largura, Math.round(largura * 284 / 1920));
}

/** Faixa lima reta do rodapé, com o fio branco em cima (Aguarde). */
export function desenharFaixaInferior (scene, topo = 668) {
    const g = scene.add.graphics();
    g.fillStyle(ARCADE_HEX.LIMA, 1).fillRect(BORDA_ESQ, topo, BORDA_DIR - BORDA_ESQ, GAME.HEIGHT - topo);
    g.fillStyle(ARCADE_HEX.BRANCO, 1).fillRect(BORDA_ESQ, topo - 3, BORDA_DIR - BORDA_ESQ, 3);
    return g;
}

/** Faixa lima com o ressalto central (Login). */
export function desenharFaixaComRessalto (scene) {
    const base = 743, alto = 720;
    const pontos = [
        [BORDA_ESQ, base], [390, base], [465, alto], [815, alto], [888, base], [BORDA_DIR, base]
    ];

    const g = scene.add.graphics();
    g.fillStyle(ARCADE_HEX.LIMA, 1).beginPath().moveTo(BORDA_ESQ, GAME.HEIGHT);
    pontos.forEach(([x, y]) => g.lineTo(x, y));
    g.lineTo(BORDA_DIR, GAME.HEIGHT).closePath().fillPath();

    g.lineStyle(3, ARCADE_HEX.BRANCO, 1).beginPath().moveTo(...pontos[0]);
    pontos.slice(1).forEach(([x, y]) => g.lineTo(x, y));
    g.strokePath();
    return g;
}

/**
 * Página de fundo lima (Materiais, Mural de Insights, Ranking): cabeçalho
 * grafite com ressalto, título, "grupo • nome" e a marca das quatro barras.
 */
export function desenharPaginaLima (scene, { titulo, rotuloJogador = '', rotuloY = 124, espacamento = null }) {
    if (scene.textures.exists(TEX.BG_LIMA)) {
        desenharImagemDeFundo(scene, TEX.BG_LIMA);
    } else {
        scene.cameras.main.setBackgroundColor(ARCADE_HEX.LIMA);
    }

    const base = 129, baixo = 151.5;
    const pontos = [
        [BORDA_ESQ, base], [393, base], [465, baixo], [815, baixo], [888, base], [BORDA_DIR, base]
    ];

    const g = scene.add.graphics();
    g.fillStyle(ARCADE_HEX.TINTA, 1).beginPath().moveTo(BORDA_ESQ, 0);
    pontos.forEach(([x, y]) => g.lineTo(x, y));
    g.lineTo(BORDA_DIR, 0).closePath().fillPath();

    g.lineStyle(3, ARCADE_HEX.BRANCO, 1).beginPath().moveTo(...pontos[0]);
    pontos.slice(1).forEach(([x, y]) => g.lineTo(x, y));
    g.strokePath();

    // Marca "////": quatro barras brancas inclinadas.
    g.fillStyle(ARCADE_HEX.BRANCO, 1);
    for (let i = 0; i < 4; i++) {
        const x = 1165 + i * 13;
        g.fillPoints([
            { x: x + 10, y: 44 }, { x: x + 16, y: 44 }, { x: x + 6, y: 84 }, { x, y: 84 }
        ], true);
    }

    scene.add.text(640, 63, titulo.toUpperCase(), ARCADE_TEXT.tituloPagina).setOrigin(0.5);
    const [grupo, nome] = rotuloJogador.split(' • ');
    const estilo = espacamento === null ? ARCADE_TEXT.jogador : { ...ARCADE_TEXT.jogador, letterSpacing: espacamento };
    desenharParComPonto(scene, 640, rotuloY, grupo ?? '', nome ?? '', estilo, ARCADE_HEX.BRANCO);
}

/**
 * "ESQUERDA ▪ DIREITA" centrado em `x`: a Silkscreen não tem o glifo "•",
 * então o ponto é um quadrado desenhado, como no Figma.
 */
export function desenharParComPonto (scene, x, y, esquerda, direita, estilo, corPonto, vao = 8.35) {
    const a = scene.add.text(0, y, esquerda, estilo).setOrigin(0, 0.5);
    const b = scene.add.text(0, y, direita, estilo).setOrigin(0, 0.5);
    const lado = 4;
    const temPonto = esquerda !== '' && direita !== '';
    const total = a.width + b.width + (temPonto ? lado + 2 * vao : 0);

    a.x = x - total / 2;
    b.x = a.x + a.width + (temPonto ? lado + 2 * vao : 0);
    const ponto = scene.add.rectangle(a.x + a.width + vao, y + 1, lado, lado, corPonto)
        .setOrigin(0, 0.5).setVisible(temPonto);
    return [a, ponto, b];
}

/** "← VOLTAR" em pixel; devolve a área de toque (≥ 44 px de altura). */
export function desenharVoltar (scene, x, y, aoClicar) {
    const g = scene.add.graphics();
    g.fillStyle(ARCADE_HEX.TINTA, 1);
    // Seta em blocos de 3 px: haste + ponta.
    g.fillRect(x + 3, y - 1.5, 13, 3);
    [[0, 0], [3, -3], [3, 3], [6, -6], [6, 6]].forEach(([dx, dy]) => g.fillRect(x + dx, y + dy - 1.5, 3, 3));

    const rotulo = scene.add.text(x + 24, y - 1, t('geral.voltar'), ARCADE_TEXT.voltar).setOrigin(0, 0.5);

    return scene.add.zone(x - 8, y, rotulo.width + 48, 48).setOrigin(0, 0.5)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', aoClicar);
}

/** Painel branco de borda preta com cantos em degrau (pop-ups). */
export function desenharPainel (g, x, y, w, h) {
    const u = 6;
    const forma = (r) => {
        // Retângulo com dois degraus de `u` em cada canto.
        const [x0, y0, x1, y1] = [x + r, y + r, x + w - r, y + h - r];
        return [
            [x0 + 2 * u, y0], [x1 - 2 * u, y0], [x1 - 2 * u, y0 + u], [x1 - u, y0 + u],
            [x1 - u, y0 + 2 * u], [x1, y0 + 2 * u], [x1, y1 - 2 * u], [x1 - u, y1 - 2 * u],
            [x1 - u, y1 - u], [x1 - 2 * u, y1 - u], [x1 - 2 * u, y1], [x0 + 2 * u, y1],
            [x0 + 2 * u, y1 - u], [x0 + u, y1 - u], [x0 + u, y1 - 2 * u], [x0, y1 - 2 * u],
            [x0, y0 + 2 * u], [x0 + u, y0 + 2 * u], [x0 + u, y0 + u], [x0 + 2 * u, y0 + u]
        ].map(([px, py]) => ({ x: px, y: py }));
    };
    g.fillStyle(ARCADE_HEX.TINTA, 1).fillPoints(forma(0), true);
    g.fillStyle(ARCADE_HEX.BRANCO, 1).fillPoints(forma(u), true);
}

/**
 * Painel de borda dupla com cantos em degrau. `cores`: fio de fora, vão,
 * fio de dentro e fundo.
 */
export function desenharPainelDuplo (g, x, y, w, h, { fora, vao, dentro, fundo }) {
    const u = 5;
    const forma = (r) => {
        const [x0, y0, x1, y1] = [x + r, y + r, x + w - r, y + h - r];
        return [
            [x0 + 2 * u, y0], [x1 - 2 * u, y0], [x1 - 2 * u, y0 + u], [x1 - u, y0 + u],
            [x1 - u, y0 + 2 * u], [x1, y0 + 2 * u], [x1, y1 - 2 * u], [x1 - u, y1 - 2 * u],
            [x1 - u, y1 - u], [x1 - 2 * u, y1 - u], [x1 - 2 * u, y1], [x0 + 2 * u, y1],
            [x0 + 2 * u, y1 - u], [x0 + u, y1 - u], [x0 + u, y1 - 2 * u], [x0, y1 - 2 * u],
            [x0, y0 + 2 * u], [x0 + u, y0 + 2 * u], [x0 + u, y0 + u], [x0 + 2 * u, y0 + u]
        ].map(([px, py]) => ({ x: px, y: py }));
    };
    g.fillStyle(fora, 1).fillPoints(forma(0), true);
    g.fillStyle(vao, 1).fillPoints(forma(4), true);
    g.fillStyle(dentro, 1).fillPoints(forma(8), true);
    g.fillStyle(fundo, 1).fillPoints(forma(12), true);
}

/**
 * Painel escuro de borda dupla branca com cantos em degrau (modais das
 * fases: instrução, material liberado, fase concluída).
 */
export function desenharPainelEscuro (g, x, y, w, h) {
    desenharPainelDuplo(g, x, y, w, h, { fora: ARCADE_HEX.BRANCO, vao: 0x2a2a2a, dentro: ARCADE_HEX.BRANCO, fundo: 0x070707 });
}
